import FieldMessage from './FieldMessage';
import React, { ChangeEvent, useRef } from 'react';
import TextField from '@atlaskit/textfield';
import { Field, FieldProps } from '@atlaskit/form';
import {hasInvalidChars} from '../helpers/StringHelpers';
import VirtualKeyboard from "./VirtualKeyboardWrapper";
import { SimpleKeyboard } from 'react-simple-keyboard';
import FieldBrowserSupport from '../helpers/FieldBrowserSupport'


const type = (isPassword?: boolean, isNumber?: boolean): string => {
  if (isPassword){
    return "password"
  }
  else if (isNumber){
    return "number"
  }
  else{
    return "text"
  }
}

const validation = (
  value?: string,
  customValidation?: (value?: string) => boolean
  ): void | string | Promise<string | void> => {
      if (customValidation && !customValidation(value)) {
        return "CUSTOM_ERROR";
      } else if (value && hasInvalidChars(value)) {
        return "INVALID_CHARS";
      } else if (!value) {
        return "EMPTY";
      } else {
        return undefined;
      }
  };

export interface ITextBoxWithKeyboardProps {
  id?: string;
  label?: string;
  placeholder?: string;
  value?: string;
  isPassword?: boolean;
  isNumber?: boolean;
  step?: number;
  required?: boolean;
  disabled?: boolean;
  onChange?: (value: string) => void;
  doNotValidate?: boolean;
  validate?: (value: string) => boolean;
  invalid?: string;
  valid?: string;
  testId?: string;
  isSpellCheckEnabled?: boolean;
  autoComplete?:string;
  inModal: boolean;
  keyboardVisibleId?: string;
  setKeyboardVisibleId?: (id: string | undefined) => void;
}

const CutOutFieldProps = (props: FieldProps<string>) => {
  const {value, ...rest} = props;
  return rest;
}

const onChange = (input: string, props: ITextBoxWithKeyboardProps) => {
  if (props.onChange) {
    props.onChange(input);
  }
};

const onChangeInput = (event: ChangeEvent<HTMLInputElement>, props: ITextBoxWithKeyboardProps, keyboardRef: React.MutableRefObject<SimpleKeyboard | null>): void => {
  const input = event.target.value;
  if (props.onChange) {
    props.onChange(input);
  }
  if (keyboardRef.current)
    keyboardRef.current.setInput(input);
};

const TextBoxWithKeyboard = (props: ITextBoxWithKeyboardProps) => {
  const keyboardRef = useRef<SimpleKeyboard | null>(null);
  const showKeyboard = props.keyboardVisibleId === props.id;

  // Sync keyboard input with props.value only when keyboard is first shown
  const prevShowKeyboard = useRef(false);
  React.useEffect(() => {
    if (showKeyboard && !prevShowKeyboard.current && keyboardRef.current) {
      keyboardRef.current.setInput(props.value || "");
    }
    prevShowKeyboard.current = showKeyboard;
  }, [showKeyboard, props.value]);

  // Hide keyboard on blur, but not if focus moves to keyboard
  const handleClick = () => {
    if (props.setKeyboardVisibleId && props.keyboardVisibleId !== props.id) {
      props.setKeyboardVisibleId(undefined);
    }
  };

  return (
      <Field 
        label={props.label || undefined}
        name={props.testId ? props.testId : "TextBoxField"}
        defaultValue={props.value || ""}
        isRequired={props.required || false}
        isDisabled={props.disabled || false}
        validate={(value: string | undefined) => {
          if(props.doNotValidate){
            return undefined;
          } else {
            return validation(value, props.validate)
          }
        }}
      >
        {
         (fieldData) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TextField
              {...CutOutFieldProps(fieldData.fieldProps)}
              testId={props.testId}
              onChange={(event: ChangeEvent<HTMLInputElement>) => onChangeInput(event, props, keyboardRef)}
              placeholder={props.placeholder || ""}
              type={type(props.isPassword, props.isNumber)}
              step={props.step? props.step: 1}
              autoComplete={props.autoComplete ? props.autoComplete : "off"}
              spellCheck={props.isSpellCheckEnabled === undefined ? true : props.isSpellCheckEnabled }
              value={props.value}
              onClick={handleClick}
            />
            {FieldBrowserSupport.isFieldBrowser() &&
              <VirtualKeyboard 
              keyboardRef={r => (keyboardRef.current = r)}
              onChange={(input: string) => onChange(input, props)} 
              show={showKeyboard}
              onShowChange={v => {
                if (props.setKeyboardVisibleId) {
                  if (v && props.keyboardVisibleId !== props.id) {
                    props.setKeyboardVisibleId(props.id);
                  } else if (!v && props.keyboardVisibleId === props.id) {
                    props.setKeyboardVisibleId(undefined);
                  }
                }
              }}
              inModal={props.inModal}
              />
            }
            {!props.doNotValidate && <FieldMessage 
              isValid={fieldData.meta.valid}
              error={fieldData.error? fieldData.error : fieldData.meta.error}
              doNotValidate={props.doNotValidate}
              validMessage={props.valid}
              errorMessage={props.invalid}
            />}
          </div>
        )}
      </Field>
  );
}

export default TextBoxWithKeyboard;

